import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import API from "../../Network/API";
import toast from "react-hot-toast";
import { USER_LOGGED_IN } from "../../Reducer/authSlice";
import moment from 'moment-timezone';
import { default as Select } from "react-select";
import WaitingApproval from "./WaitingApproval";

// Selected-file preview chip: image thumbnail or type icon + name + size + remove
function FilePreview({ file, onClear }) {
    if (!file) return null;
    const isImage = file.type?.startsWith("image/");
    const size = file.size > 1024 * 1024
        ? (file.size / (1024 * 1024)).toFixed(1) + " MB"
        : Math.max(1, Math.round(file.size / 1024)) + " KB";
    return (
        <div className="file-chip mt-2">
            {isImage ? (
                <img src={URL.createObjectURL(file)} alt={file.name} className="file-chip-thumb" />
            ) : (
                <span className="file-chip-icon"><i className="fa-light fa-file-lines" /></span>
            )}
            <span className="file-chip-info">
                <span className="file-chip-name">{file.name}</span>
                <span className="file-chip-size">{size}</span>
            </span>
            <button type="button" className="file-chip-remove" title="Remove" onClick={onClear}>
                <i className="fa-light fa-xmark" />
            </button>
        </div>
    );
}

// 30-minute time options stored as 24h "HH:mm", shown as "h:mm AM/PM"
const TIME_OPTIONS = (() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
        for (const m of [0, 30]) {
            const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
            const hour12 = h % 12 === 0 ? 12 : h % 12;
            opts.push({ value, label: `${hour12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}` });
        }
    }
    return opts;
})();

const DRAFT_KEY = "tutor_profile_draft";
const readDraft = () => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch (e) { return {}; }
};

const DEFAULT_FROM = "09:00";
const DEFAULT_TO = "17:00";
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function TeacherInfo() {
    const { user, accessToken } = useSelector((state) => state.authReducer);
    const select_lang = useSelector((state) => state.languageReducer);
    const [draft] = useState(readDraft);
    const [languages, setLanguages] = useState(draft.languages?.length ? draft.languages : [{ language: '', lang_type: '' }]);
    const dispatch = useDispatch();
    const [countries, setCountry] = useState([]);
    const [language, setLanguage] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [step, setStep] = useState(draft.step ?? 1);
    const [file, setFile] = useState(null);
    const [bio, setBio] = useState(null);
    const [input, setInput] = useState(draft.input ?? {});
    const [timezones, setTimezones] = useState([]);
    const [country_id, setCountryId] = useState(draft.country_id ?? 0);
    const [time_zone, setTimeZoneId] = useState(draft.time_zone ?? null);
    const [photoURL, setPhotoURL] = useState(null);
    const [schedules, setSchedules] = useState(draft.schedules ?? {
        Monday: { from_time: '', to_time: '' },
        Tuesday: { from_time: '', to_time: '' },
        Wednesday: { from_time: '', to_time: '' },
        Thursday: { from_time: '', to_time: '' },
        Friday: { from_time: '', to_time: '' },
        Saturday: { from_time: '', to_time: '' },
        Sunday: { from_time: '', to_time: '' }
    });

    const [dbs_file, setDbsFile] = useState(null);
    const [certificate_file, setCertificateFile] = useState(null);
    const [gov_id_card, setGovIDFile] = useState(null);

    const langTypeOptions = [
        { value: 'basic', label: 'Basic' },
        { value: 'native', label: 'Native' },
        { value: 'fluent', label: 'Fluent' },
        { value: 'conversational', label: 'Conversational' },
    ];

    const addMoreFields = () => {
        setLanguages([...languages, { language: '', lang_type: '' }]);
    };

    const handleChange1 = (e) => {
        let name = e.target.name;
        let value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setInput({ ...input, [name]: value });
    };

    const handleInputChange = (day, field, value) => {
        setSchedules(prevState => ({
            ...prevState,
            [day]: {
                ...prevState[day],
                [field]: value
            }
        }));
    };

    const handleInputChangeLanguage = (index, field, value) => {
        const newLanguages = [...languages];
        newLanguages[index][field] = value;
        setLanguages(newLanguages);
    };

    const removeField = (index) => {
        const newLanguages = [...languages];
        newLanguages.splice(index, 1);
        setLanguages(newLanguages);
    };



    // autosave draft so a refresh never loses progress (files can't be persisted)
    useEffect(() => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ input, languages, schedules, time_zone, country_id, step }));
    }, [input, languages, schedules, time_zone, country_id, step]);

    useEffect(() => {
        if ((draft.step ?? 1) > 1 || Object.keys(draft.input ?? {}).length > 0) {
            toast('Welcome back — your saved progress was restored', { icon: '💾' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const tzNames = moment.tz.names();
        const newOptions = tzNames.map(tz => ({
            value: tz, // Assuming you want the ID as a string
            label: tz
        }));
        setTimezones(newOptions);

        get_country();

    }, []);

    const get_country = () => {
        setLoading(true);


        API.get(`/v1/oauth/countries`).then((response) => {
            // setCountry(response.data.data.countries);
            const newOptions = response.data.data.countries.map(item => ({
                value: item.code, // Assuming you want the ID as a string
                label: item.name
            }));
            setCountry(newOptions);
            setLoading(false);
        }).catch((error) => {
            setLoading(false);
        });
    }

    const validateStep = (s) => {
        if (s === 1) {
            const name = (input?.name ?? user?.name ?? '').trim();
            if (!name) { toast.error('Please enter your name'); return false; }
            if (!country_id) { toast.error('Please select your country'); return false; }
            if (!input?.age_verify) { toast.error('Please confirm that you are over 18'); return false; }
        }
        if (s === 2 && !file && !photoURL) {
            toast.error('Please upload a profile photo'); return false;
        }
        if (s === 3 && !languages.some(l => l.language && l.lang_type)) {
            toast.error('Please add at least one language with a level'); return false;
        }
        if (s === 4) {
            if (!time_zone) { toast.error('Please choose your timezone'); return false; }
            if (!Object.values(schedules).some(d => d.from_time && d.to_time)) {
                toast.error('Please set at least one day of availability'); return false;
            }
            const bad = Object.entries(schedules).find(([, t]) => t.from_time && t.to_time && t.from_time >= t.to_time);
            if (bad) { toast.error(`${bad[0]}: the end time must be after the start time`); return false; }
        }
        if (s === 5) {
            // bank info is optional (can be set later in Settings), but if one
            // field is started the pair must be complete
            const bank = String(input?.bank_name ?? '').trim();
            const acct = String(input?.account_number ?? '').trim();
            if ((bank && !acct) || (!bank && acct)) {
                toast.error('Please fill in both bank name and account number — or skip this step for now');
                return false;
            }
        }
        return true;
    }

    const nextStep = () => {
        if (!validateStep(step)) return;
        setStep(prevStep => prevStep + 1);
    }

    const prevStep = () => {
        setStep(prevStep => prevStep - 1);
    }

    const setSelectedFile = (e) => {
        setFile(e.target.files[0])
        setPhotoURL(URL.createObjectURL(e.target.files[0]))
    };

    const setSelectedDbsFile = (e) => {
        setDbsFile(e.target.files[0])

    };

    const setSelectedCertificateFile = (e) => {
        setCertificateFile(e.target.files[0])
    };

    const setSelectedGovIdFile = (e) => {
        setGovIDFile(e.target.files[0])
    };

    const onChangeCountry = (e) => {

        setCountryId(e?.value != undefined ? e?.value : '')
    };

    const onChangeTimeZone = (e) => {
        const tz = e?.value != null ? e?.value : '';
        setTimeZoneId(tz)

        // first timezone pick: pre-fill a 9 AM – 5 PM weekday schedule as a starting point
        if (tz) {
            const hasAny = Object.values(schedules).some(d => d.from_time && d.to_time);
            if (!hasAny) {
                setSchedules(prev => {
                    const next = { ...prev };
                    WEEKDAYS.forEach(d => { next[d] = { from_time: DEFAULT_FROM, to_time: DEFAULT_TO }; });
                    return next;
                });
                toast.success('Weekdays pre-filled with 9:00 AM – 5:00 PM — adjust to fit your schedule');
            }
        }
    };

    const toggleDay = (day, on) => {
        setSchedules(prev => ({
            ...prev,
            [day]: on ? { from_time: DEFAULT_FROM, to_time: DEFAULT_TO } : { from_time: '', to_time: '' }
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!time_zone) {
            toast.error("Please choose your timezone (Availability step)");
            setStep(4);
            return;
        }

        const hasSchedule = Object.values(schedules).some(d => d.from_time && d.to_time);
        if (!hasSchedule) {
            toast.error("Please set at least one day of availability");
            setStep(4);
            return;
        }

        const badDay = Object.entries(schedules).find(([, t]) => t.from_time && t.to_time && t.from_time >= t.to_time);
        if (badDay) {
            toast.error(`${badDay[0]}: the end time must be after the start time`);
            setStep(4);
            return;
        }

        const hasLanguage = languages.some(l => l.language && l.lang_type);
        if (!hasLanguage) {
            toast.error("Please add at least one language with a level");
            setStep(3);
            return;
        }

        if (!dbs_file || !certificate_file || !gov_id_card) {
            toast.error("Please upload all required documents");
            return;
        }

        setSaveLoading(true)

        const fd = new FormData();

        fd.append('bio', input?.bio ?? '')
        fd.append('country', country_id ?? '')
        fd.append('phone', input?.phone ?? '')

        if (file) {
            fd.append('image', file, file?.name)
        }

        if (dbs_file) {
            fd.append('dbs_file', dbs_file, dbs_file?.name)
        }

        if (certificate_file) {
            fd.append('certificate_file', certificate_file, certificate_file?.name)
        }
        if (gov_id_card) {
            fd.append('gov_id_card', gov_id_card, gov_id_card?.name)
        }

        if (input?.age_verify) {
            fd.append('age_verify', 1)
        } else {
            fd.append('age_verify', 0)
        }

        if (input?.check_eduction) {
            fd.append('check_eduction', 1)
        } else {
            fd.append('check_eduction', 0)
        }


        fd.append('education_institute', input?.education_institute ?? '')
        fd.append('education_level', input?.education_level ?? '')
        fd.append('education_title', input?.education_title ?? '')
        fd.append('hourly_rate', input?.hourly_rate ?? 0)
        fd.append('bank_name', input?.bank_name ?? '')
        fd.append('account_number', input?.account_number ?? '')
        fd.append('bank_additional', input?.bank_additional ?? '')
        fd.append('timezone', time_zone)

        fd.append('schedules', JSON.stringify(schedules))
        fd.append('languages', JSON.stringify(languages))

        API.post("/v1/oauth/settings/tutor_profile", fd, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: progressEvent => {
                console.log("Progress: " + progressEvent.loaded / progressEvent.total);
            }
        }).then((response) => {

            setSaveLoading(false)
            localStorage.setItem(
                'xller_auth',
                JSON.stringify({
                    user: response?.data?.data?.user,
                    accessToken: localStorage.getItem('xller_token'),
                })
            )

            const userInfo = JSON.parse(localStorage.getItem("xller_auth"));
            if (userInfo) {
                dispatch(USER_LOGGED_IN(userInfo));
            }

            localStorage.removeItem(DRAFT_KEY);
            toast.success(response.data.message || 'Profile submitted! Our team will review it shortly.');
            window.scrollTo(0, 0);
        }).catch((err) => {
            const errors = err?.response?.data?.errors;
            if (errors && typeof errors === 'object') {
                // Laravel validation errors: surface the first message of each field
                Object.values(errors).slice(0, 3).forEach((msgs) => {
                    toast.error(Array.isArray(msgs) ? msgs[0] : String(msgs));
                });
            } else {
                toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
            }
            setSaveLoading(false)
        });

    }
    return (
        <>
            {user.approved == 0 && user.teacher_info != '' ?
                <WaitingApproval />
                :
                <div className="tutor-sign-in-section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 mx-0">
                                <form id="msform" onSubmit={handleSubmit}>
                                    {/* progressbar */}
                                    <ul id="progressbar">
                                        {[
                                            { id: 'about', label: 'About' },
                                            { id: 'photos', label: 'Photos' },
                                            { id: 'education', label: 'Education' },
                                            { id: 'availability', label: 'Availability' },
                                            { id: 'bank', label: 'Bank Info' },
                                            { id: 'document', label: 'Documents' },
                                        ].map((st, i) => (
                                            <li
                                                key={st.id}
                                                id={st.id}
                                                className={`${step >= i + 1 ? 'active' : ''} ${step > i + 1 ? 'done clickable' : ''}`}
                                                onClick={() => step > i + 1 && setStep(i + 1)}
                                                title={step > i + 1 ? 'Go back to this step' : undefined}
                                            >
                                                {st.label}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="row justify-content-center">
                                        <div className="col-md-8 col-lg-6 col-sm-10 col-12">
                                            {step === 1 && (
                                                <fieldset className={`text-end about ${step === 1 ? 'd-block' : 'd-done'}`}>
                                                    <div className="form-card card mb-4">
                                                        <div className="card-body">
                                                            <div className="step-heading">
                                                                <h2>About</h2>
                                                            </div>
                                                            <div className="step-dec">
                                                                <p className="m-0">Start creating your public tutor profile. Your progress will be automatically saved as you complete each section. You can return at any time to finish your registration.</p>
                                                            </div>
                                                            <div className="step-form">
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="First_name" className="form-label">Your name</label>
                                                                        <input onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            if (/^[a-zA-Z\s]*$/.test(value)) {
                                                                                handleChange1(e);
                                                                            } else {
                                                                                toast.error('Name should only contain letters and spaces');
                                                                            }
                                                                        }} defaultValue={input?.name ?? user?.name} type="text" className="form-control rounded input-hight-44" id="First_name" placeholder="Your full name" name="name" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label className="form-label">Bio</label>
                                                                        <input defaultValue={input?.bio} onChange={(e) => handleChange1(e)} type="text" id="bio" name="bio" className="form-control input-hight-44" placeholder="write bio,ex:Speaks English (Native)" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label className="form-label">Country {isLoading ? (
                                                                            <span
                                                                                className="spinner-border spinner-border-sm ml-5"
                                                                                role="status"
                                                                                aria-hidden="true"
                                                                            ></span>
                                                                        ) : (
                                                                            <span></span>
                                                                        )}</label>
                                                                        <Select onChange={(e) => onChangeCountry(e)} options={countries} id="country" name="country" isClearable={true} value={countries.find(country => country.value === country_id) || null} placeholder="Select your country…" />


                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label className="form-label">Phone number (optional)</label>
                                                                        <input defaultValue={input?.phone} onChange={(e) => handleChange1(e)} type="text" name="phone" className="form-control input-hight-44 fc-phone" aria-label="" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group row">
                                                                    <div className="col-12">
                                                                        <div className="form-check custom-form-check mb-3">
                                                                            <input onChange={(e) => handleChange1(e)} name="age_verify" className="form-check-input custom-form-check-input" type="checkbox" checked={!!input?.age_verify} id="flexCheckDefault" />
                                                                            <label className="form-check-label custom-form-check-label" htmlFor="flexCheckDefault">
                                                                                I confirm I’m over 18
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={nextStep} className="next action-button input-hight-44 btn btn-login box-shadow-1 fw-bold">Next</button>
                                                </fieldset>
                                            )}

                                            {step === 2 && (
                                                <fieldset className="text-end photos">
                                                    <div className="form-card card mb-4">
                                                        <div className="card-body">
                                                            <div className="step-heading">
                                                                <h2>Profile photo</h2>
                                                            </div>
                                                            <div className="step-dec">
                                                                <p className="m-0">Choose a photo that will help learners get to know you.</p>
                                                            </div>
                                                            <div className="step-form">
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <div className="d-flex">

                                                                            <div className="flex-shrink-0">
                                                                                <div
                                                                                    style={{
                                                                                        backgroundImage: `url(${photoURL})`,
                                                                                        backgroundRepeat: 'no-repeat',
                                                                                        backgroundPosition: 'center',
                                                                                        backgroundSize: 'cover',
                                                                                    }}
                                                                                    className="photos-upload-inner position-relative"
                                                                                    id="cvpotos"
                                                                                >
                                                                                    <input
                                                                                        className="photos-upload-input"
                                                                                        type="file"
                                                                                        onChange={(e) => setSelectedFile(e)} accept=".jpg,.png"
                                                                                    />
                                                                                    <div className={`photos-upload-text ${photoURL!=null ? 'd-none' : ''}`}>
                                                                                        <p className="m-0">JPG or PNG, max 5MB</p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex-grow-1 ms-3">
                                                                                <div className="step-upload-photos-name d-flex mb-2 align-items-center">
                                                                                    <h4 className="me-2 fs-24-600 m-0">{user?.name}</h4>
                                                                                </div>
                                                                                <div className="step-upload-photos-text d-flex mb-2 d-none">
                                                                                    <i className="fa-light fa-graduation-cap me-2" />
                                                                                    <p className="m-0">Teaches English lessons</p>
                                                                                </div>
                                                                                <div className={`step-upload-photos-text d-flex ${bio == '' ? 'd-none' : ''}`}>
                                                                                    <i className="fa-light fa-quote-left me-2" />
                                                                                    <p className="m-0">{bio}</p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={prevStep} className="next action-button input-hight-44 btn btn-secondary box-shadow-1 fw-bold me-1">Back</button>
                                                    <button type="button" onClick={nextStep} className="next action-button input-hight-44 btn btn-login box-shadow-1 fw-bold">Next</button>
                                                </fieldset>
                                            )}

                                            {step === 3 && (
                                                <fieldset className="text-end education">
                                                    <div className="form-card card mb-4">
                                                        <div className="card-body">
                                                            <div className="step-heading">
                                                                <h2>Education</h2>
                                                            </div>
                                                            <div className="step-dec">
                                                                <p className="m-0">Tell students more about the higher education that you've completed or are working on.</p>
                                                            </div>
                                                            <div className="step-form">
                                                                <div className="form-group mb-3 row d-none">
                                                                    <div className="col-12">
                                                                        <div className="form-check custom-form-check mb-3">
                                                                            <input onChange={(e) => handleChange1(e)} name="check_eduction" className="form-check-input custom-form-check-input" type="checkbox" id="flexCheckDefault1" />
                                                                            <label className="form-check-label custom-form-check-label" htmlFor="flexCheckDefault1">
                                                                                I don't have a higher education degree
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="education_institue" className="form-label">University</label>
                                                                        <input  defaultValue={input?.education_institute} onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            if (/^[a-zA-Z\s]*$/.test(value)) {
                                                                                handleChange1(e);
                                                                            } else {
                                                                                toast.error('The contain should be letters and spaces');
                                                                            }
                                                                        }} name="education_institute" type="text" className="form-control rounded input-hight-44" id="education_institue" placeholder="E.g. Mount Royal University" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="education_level" className="form-label">Degree</label>
                                                                        <input  defaultValue={input?.education_level} onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            if (/^[a-zA-Z\s]*$/.test(value)) {
                                                                                handleChange1(e);
                                                                            } else {
                                                                                toast.error('The contain should be letters and spaces');
                                                                            }
                                                                        }} name="education_level" type="text" className="form-control rounded input-hight-44" id="education_level" placeholder="E.g. Bachelor's degree in the English Language" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="education_title" className="form-label">Education Title</label>
                                                                        <input defaultValue={input?.education_title} onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            if (/^[a-zA-Z\s]*$/.test(value)) {
                                                                                handleChange1(e);
                                                                            } else {
                                                                                toast.error('The contain should be letters and spaces');
                                                                            }
                                                                        }} name="education_title" type="text" className="form-control rounded input-hight-44" id="education_title" placeholder="E.g. English Native" />
                                                                    </div>
                                                                </div>
                                                                <div className="step-heading">
                                                                    <h2>Language</h2>
                                                                </div>
                                                                <div id="language">
                                                                    {languages.map((lang, index) => (
                                                                        <div className="form-group mb-2 row" key={index}>
                                                                            <div className="col-12">
                                                                                <div className="input-group">

                                                                                    <select name="language_id" className="form-select" onChange={(e) =>
                                                                                        handleInputChangeLanguage(index, 'language', e.target.value)
                                                                                    }>
                                                                                        <option value={``}></option>
                                                                                        {select_lang?.data?.map((option) => (
                                                                                            <option selected={option.id === lang.language} key={option.value} value={option.id}>
                                                                                                {option.name}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>

                                                                                    <select
                                                                                        name="lang_type[]"
                                                                                        className="form-select"
                                                                                        onChange={(e) => handleInputChangeLanguage(index, 'lang_type', e.target.value)}
                                                                                        value={lang.lang_type} // Use the saved value for language type
                                                                                    >
                                                                                        <option value={``}></option>
                                                                                        {langTypeOptions.map((option) => (
                                                                                            <option key={option.value} value={option.value}>
                                                                                                {option.label}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                    {index === languages.length - 1 && (
                                                                                        <button className="input-group-text" type="button" onClick={addMoreFields}>
                                                                                            <i className="fa-solid fa-plus-circle"></i>
                                                                                        </button>
                                                                                    )}
                                                                                    {index > 0 && (
                                                                                        <button className="input-group-text" type="button" onClick={() => removeField(index)}>
                                                                                            <i className="fa-solid fa-minus-circle text-danger"></i>
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={prevStep} className="next action-button input-hight-44 btn btn-secondary box-shadow-1 fw-bold me-1">Back</button>
                                                    <button type="button" onClick={nextStep} className="next action-button input-hight-44 btn btn-login box-shadow-1 fw-bold">Next</button>
                                                </fieldset>
                                            )}

                                            {step === 4 && (
                                                <fieldset className="text-end availability">
                                                    <div className="form-card card mb-4">
                                                        <div className="card-body">
                                                            <div className="step-heading">
                                                                <h2>Availability</h2>
                                                            </div>
                                                            <div className="step-form mb-5">
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label className="form-label ">Choose your timezone </label>
                                                                        <Select onChange={(e) => onChangeTimeZone(e)} options={timezones}
                                                                            id="timezone" name="timezone"
                                                                            isClearable={true}
                                                                            value={timezones.find(option => option.value === time_zone) || null}
                                                                            placeholder="Select your timezone…"
                                                                        />


                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="step-dec">
                                                                <p className="m-0">Let students know when you're available for lessons. Toggle a day on and pick your hours.</p>
                                                            </div>

                                                            <div className="step-form text-start">
                                                                {Object.keys(schedules).map(day => {
                                                                    const active = !!(schedules[day].from_time && schedules[day].to_time);
                                                                    const invalid = active && schedules[day].from_time >= schedules[day].to_time;
                                                                    return (
                                                                        <div key={day} className={`availability-day ${active ? 'active' : ''}`}>
                                                                            <div className="form-check form-switch availability-day-toggle mb-0">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="checkbox"
                                                                                    role="switch"
                                                                                    id={`day-${day}`}
                                                                                    checked={active}
                                                                                    onChange={(e) => toggleDay(day, e.target.checked)}
                                                                                />
                                                                                <label className="form-check-label" htmlFor={`day-${day}`}>{day}</label>
                                                                            </div>
                                                                            {active ? (
                                                                                <div className="availability-day-times">
                                                                                    <select
                                                                                        className={`form-select ${invalid ? 'is-invalid' : ''}`}
                                                                                        value={schedules[day].from_time}
                                                                                        onChange={(e) => handleInputChange(day, 'from_time', e.target.value)}
                                                                                        aria-label={`${day} from`}
                                                                                    >
                                                                                        {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                                                    </select>
                                                                                    <span className="availability-sep">–</span>
                                                                                    <select
                                                                                        className={`form-select ${invalid ? 'is-invalid' : ''}`}
                                                                                        value={schedules[day].to_time}
                                                                                        onChange={(e) => handleInputChange(day, 'to_time', e.target.value)}
                                                                                        aria-label={`${day} to`}
                                                                                    >
                                                                                        {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                                                    </select>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="availability-unavailable">Unavailable</span>
                                                                            )}
                                                                            {invalid && <div className="availability-error w-100">End time must be after start time</div>}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={prevStep} className="next action-button input-hight-44 btn btn-secondary box-shadow-1 fw-bold me-1">Back</button>
                                                    <button type="button" onClick={nextStep} className="next action-button input-hight-44 btn btn-login box-shadow-1 fw-bold">Next</button>
                                                </fieldset>
                                            )}

                                            {step === 5 && (
                                                <fieldset className="text-end">
                                                    <div className="form-card card mb-4">
                                                        <div className="card-body">
                                                            <div className="step-heading">
                                                                <h2>Bank Info <span className="optional-badge">Optional</span></h2>
                                                            </div>
                                                            <div className="step-dec">
                                                                <p className="m-0">Add your hourly rate and payout details now, or skip this step and set them up anytime later in Settings → Bank Account.</p>
                                                            </div>
                                                            <div className="step-form">

                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="bank_name" className="form-label">Bank Name</label>
                                                                        <input defaultValue={input?.bank_name} name="bank_name" onChange={(e) => handleChange1(e)} type="text" className="form-control rounded input-hight-44" id="bank_name" placeholder="" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="account_number" className="form-label">Account Number</label>
                                                                        <input defaultValue={input?.account_number} name="account_number" onChange={(e) => handleChange1(e)} type="text" className="form-control rounded input-hight-44" id="account_number" placeholder="" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="bank_additional" className="form-label">Other(optional)</label>
                                                                        <input defaultValue={input?.bank_additional} name="bank_additional" onChange={(e) => handleChange1(e)} type="text" className="form-control rounded input-hight-44" id="bank_additional" placeholder="" />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="rate" className="form-label">Hourly Rate</label>
                                                                        <input defaultValue={input?.hourly_rate} name="hourly_rate" onChange={(e) => handleChange1(e)} type="number" min={0} className="form-control rounded input-hight-44" id="rate" placeholder="0" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={prevStep} className="next action-button input-hight-44 btn btn-secondary box-shadow-1 fw-bold me-1">Back</button>
                                                    <button type="button" onClick={() => setStep(6)} className="next action-button input-hight-44 btn btn-secondary box-shadow-1 fw-bold me-1">Skip for now</button>
                                                    <button type="button" onClick={nextStep} className="next action-button input-hight-44 btn btn-login box-shadow-1 fw-bold">Next</button>
                                                </fieldset>
                                            )}

                                            {step === 6 && (
                                                <fieldset className="text-end document">
                                                    <div className="form-card card mb-4">
                                                        <div className="card-body">
                                                            <div className="step-heading">
                                                                <h2>Document</h2>
                                                            </div>
                                                            <div className="step-subheading">
                                                                <h5 className="fw-bold">Please upload below documents</h5>
                                                            </div>
                                                            <div className="step-dec">

                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="dbs" className="form-label">DBS</label>
                                                                        <input required={!dbs_file} key={dbs_file ? "set" : "empty"} onChange={(e) => setSelectedDbsFile(e)} type="file" className="form-control rounded input-hight-44" id="dbs" accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"/>
                                                                        <FilePreview file={dbs_file} onClear={() => setDbsFile(null)} />
                                                                    </div>
                                                                </div>

                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="education_cert" className="form-label">Education  Certificate</label>
                                                                        <input required={!certificate_file} key={certificate_file ? "set" : "empty"} onChange={(e) => setSelectedCertificateFile(e)} type="file" className="form-control rounded input-hight-44" id="education_cert" accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"/>
                                                                        <FilePreview file={certificate_file} onClear={() => setCertificateFile(null)} />
                                                                    </div>
                                                                </div>

                                                                <div className="form-group mb-3 row">
                                                                    <div className="col-12">
                                                                        <label htmlFor="gov_id" className="form-label">Government ID / Passport</label>
                                                                        <input required={!gov_id_card} key={gov_id_card ? "set" : "empty"} onChange={(e) => setSelectedGovIdFile(e)} type="file" className="form-control rounded input-hight-44" id="gov_id" accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"/>
                                                                        <FilePreview file={gov_id_card} onClear={() => setGovIDFile(null)} />
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={prevStep} className="next action-button input-hight-44 btn btn-secondary box-shadow-1 fw-bold me-1">Back</button>
                                                    <button type="submit" disabled={saveLoading} className="next action-button input-hight-44 btn btn-login box-shadow-1 fw-bold">
                                                        {saveLoading ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Submitting…</>) : ('Submit for Review')}
                                                    </button>
                                                </fieldset>
                                            )}


                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </>
    );
}
